import { Button, Spacer, Text } from '@sparemin/blockhead';
import React from 'react';

import AutomationPostModalAdditionalDetails from 'components/AutomationPostModalAdditionalDetails';
import AutomationPostModalHeader from 'components/AutomationPostModalHeader';

import CaptionsLearnCenterLink from 'components/CaptionsLearnLink';
import DynamicElementsPopper from 'components/DynamicElementsPopper';
import EducationalCard from 'components/EducationalCard/EducationalCard';
import ModalContentWithMedia from 'components/Modal/ModalContentWithMedia';
import ShareFormTextarea from 'components/ShareForm/ShareFormTextarea';
import SocialAccountInfo from 'components/SocialAccountInfo';
import { Step } from 'components/SteppedModal';

import { MAX_DESCRIPTION_CHARS } from 'containers/BlueskyPostModal/constants';
import useBlueskyPostModalSelector from 'containers/BlueskyPostModal/useBlueskyPostModalSelector';
import { getUsernameHandle } from 'utils/social-platforms';
import { BlueskyAutoPostModalSteps } from '../types';
import { block } from '../utils';
import { captionsMetadata } from './constants';
import { UsePostComposerBaseConfig } from './types';

export interface UsePostComposerStepConfig extends UsePostComposerBaseConfig {
  description: string;
  allowSubmit: boolean;
  onDescriptionChange: (newDescription: string) => void;
  onSwitchAccount?: () => void;
  onAdditionalDetailsClick?: () => void;
  onHideModal?: () => void;
  onSubmit?: () => void;
}

export default function usePostComposerStep({
  description,
  allowSubmit,
  onDescriptionChange,
  onSwitchAccount,
  onAdditionalDetailsClick,
  onHideModal,
  onSubmit,
}: UsePostComposerStepConfig): Step<BlueskyAutoPostModalSteps> {
  const { username, avatarSrc } = useBlueskyPostModalSelector();

  return {
    id: 'composer',
    hideFooter: true,
    replaceBody: true,
    component: (
      <>
        <ModalContentWithMedia
          className="composer-step-content"
          onClose={onHideModal}
        >
          <ModalContentWithMedia.ModalContentWithMediaColumn>
            <img
              src="https://static.sparemin.com/static/webapp-assets/auto-posting-detail/auto-posting-detail-bluesky-image-202506122020.png"
              className={block('composer-image')}
            />
          </ModalContentWithMedia.ModalContentWithMediaColumn>

          <ModalContentWithMedia.ModalContentWithMediaColumn className="composer-content-column">
            <Spacer space="20px" orientation="vertical" align="flex-start">
              <SocialAccountInfo
                avatarSrc={avatarSrc}
                username={getUsernameHandle(username)}
                onSwitchAccountClick={onSwitchAccount}
              />

              <AutomationPostModalHeader
                title="Let’s set up your video details"
                description="We'll post these details with each video. You can pull information from your RSS feed, such as episode titles, to make each post unique."
              />

              <ShareFormTextarea
                hideProgress
                type="textarea"
                maxChars={MAX_DESCRIPTION_CHARS}
                value={description}
                className={block('description-container')}
                controlClassName={block('description-input')}
                label={
                  <Text variant="label">
                    <b>Example of your video descriptions</b>{' '}
                    <CaptionsLearnCenterLink />
                  </Text>
                }
                onChange={onDescriptionChange}
              />

              <EducationalCard>
                <DynamicElementsPopper
                  buttonContent="add content from your rss"
                  elementsList={captionsMetadata}
                  onClick={metaValue =>
                    onDescriptionChange(`${description} ${metaValue}`)
                  }
                />
              </EducationalCard>
            </Spacer>

            <Spacer
              orientation="vertical"
              space="20px"
              align="flex-start"
              justify="space-between"
              className={block('composer-bottom')}
            >
              <AutomationPostModalAdditionalDetails
                description="Adjust quote and reply settings."
                onClick={onAdditionalDetailsClick}
              />

              <Button
                fluid
                variant="solid"
                onPress={onSubmit}
                isDisabled={!allowSubmit}
              >
                confirm auto-posting details
              </Button>
            </Spacer>
          </ModalContentWithMedia.ModalContentWithMediaColumn>
        </ModalContentWithMedia>
      </>
    ),
  };
}
